import {
    DarkMode as DarkModeIcon,
    ExpandLess,
    ExpandMore,
    LightMode as LightModeIcon,
    Logout as LogoutIcon,
    PersonOutline as PersonOutlineIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import {
    Collapse,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useUserService } from '../../services/user.service';

interface UserMenuProps {
    onNavigate?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const { isDarkMode, toggleTheme } = useAppTheme();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const userService = useUserService();
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleSettings = () => {
        setOpen(false);
        navigate('/settings');
        if (onNavigate) onNavigate();
    };

    const handleThemeToggle = () => {
        setOpen(false);
        toggleTheme();
        if (onNavigate) onNavigate();
    };

    const handleUserLogout = async () => {
        setOpen(false);
        try {
            userService.log('Logout');
            await logout();
            navigate('/login');
            if (onNavigate) onNavigate();
        } catch (error) {
            console.error('Failed to log out:', error);
        }
    };

    return (
        <>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                    component="div"
                    disablePadding
                    sx={{
                        mb: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}
                >
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleSettings}
                            sx={{
                                borderRadius: 0,
                                mb: 0.75,
                                py: isMobile ? 1.5 : 1.25,
                                px: 1.5,
                                color: (theme) =>
                                    theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary,
                                position: 'relative',
                                pl: '16px',
                                '&:hover': {
                                    bgcolor: theme.palette.primary.main,
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 34,
                                    color: 'inherit',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1.4rem',
                                    },
                                }}
                            >
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('sidebar.menu.settings')}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleThemeToggle}
                            sx={{
                                borderRadius: 0,
                                mb: 0.75,
                                py: isMobile ? 1.5 : 1.25,
                                px: 1.5,
                                color: (theme) =>
                                    theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary,
                                position: 'relative',
                                pl: '16px',
                                '&:hover': {
                                    bgcolor: theme.palette.primary.main,
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 34,
                                    color: 'inherit',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1.4rem',
                                    },
                                }}
                            >
                                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                            </ListItemIcon>
                            <ListItemText
                                primary={isDarkMode ? t('sidebar.menu.lightMode') : t('sidebar.menu.darkMode')}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleUserLogout}
                            sx={{
                                borderRadius: 0,
                                mb: 0.75,
                                py: isMobile ? 1.5 : 1.25,
                                px: 1.5,
                                color: (theme) =>
                                    theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary,
                                position: 'relative',
                                pl: '16px',
                                '&:hover': {
                                    bgcolor: theme.palette.primary.main,
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 34,
                                    color: 'inherit',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1.4rem',
                                    },
                                }}
                            >
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('sidebar.menu.logout')}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Collapse>
            <ListItem
                disablePadding
                sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    color: (theme) => (theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary),
                    position: 'relative',
                }}
            >
                <ListItemButton
                    onClick={handleToggle}
                    sx={{
                        borderRadius: 1,
                        mb: 0.75,
                        py: isMobile ? 1.5 : 1.25,
                        px: 1.5,
                        color: (theme) => (theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary),
                        position: 'relative',
                        '&:hover': {
                            bgcolor: theme.palette.primary.main,
                            color: '#ffffff',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 34,
                            color: 'inherit',
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.4rem',
                            },
                        }}
                    >
                        <PersonOutlineIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary={t('sidebar.menu.account')}
                        primaryTypographyProps={{
                            sx: {
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            },
                        }}
                    />
                    {open ? <ExpandMore /> : <ExpandLess />}
                </ListItemButton>
            </ListItem>
        </>
    );
};

export default UserMenu;
